import { YesNoConfirmModal } from "@common/components/modals";
import { useApp } from '@common/hooks';
import { AppointmentManagementItem, IAppointmentManagementGroupingModel, IAppointmentManagementItem } from '@common/models';
import { VisitService } from "@common/services";
import { anyPromiseTrackerTagsInProgress, ordinal } from '@common/utils';
import { normalizePhone } from '@common/utils/phoneUtils';
import { CancelReservationModal, ConfirmReservationModal } from '@customer/components/modals';
import { AppointmentsStore } from '@customer/stores';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, ListGroup, Row, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

interface IAppointmentGroupProps {
    store: AppointmentsStore;
    group: IAppointmentManagementGroupingModel;
    groupId: string;
}

export const AppointmentGroup = observer(({ store, group, groupId }: IAppointmentGroupProps) => {
    const { configuration } = useApp();
    const [resendEmail, setResendEmail] = useState<boolean>(false);
    const [rescheduleItem, setRescheduleItem] = useState<AppointmentManagementItem | undefined>(undefined);

    const navigate = useNavigate();
    const clinicDate = new Date(group.clinicDate);
    const mounted = useRef(true);
    const [selectedGroup, setSelectedGroup] = useState<string>('');

    const friendlyClinicDate = useMemo(() => {
        return `${DateTime.fromJSDate(group.clinicDate).toFormat("EEEE, MMMM")} ${ordinal.getNumberOrdinal(group.clinicDate.getDate())}`;

    }, [group.clinicDate]);

    const cancellationBody = useMemo(() => {
        if (!store.isReschedule && !store.isRebook) {
            return `Are you sure? This will Cancel your appointment(s) for ${store.petNames} on ${friendlyClinicDate}.`;
        } else {
            return `Are you sure? This will Cancel your appointment(s) for ${store.petNames} and allow you to rebook.`;
        }
    }, [friendlyClinicDate, store.isRebook, store.isReschedule, store.petNames]);

    const confirmationBody = useMemo(() => {
        return `Your pet's appointment on ${friendlyClinicDate} has been confirmed. We look forward to seeing you. To help us prepare for your visit please complete your Pet's Intake Form prior to your appointment. Forms are available 48 hours prior to your visit.`;
    }, [friendlyClinicDate]);

    //#region Events

    const onConfirmGroupClick = () => {
        store.confirmAppointmentGroup(group);
    }


    const onCancelItemClick = (item: IAppointmentManagementItem, selectedGroupId: string) => {
        setSelectedGroup(selectedGroupId);
        store.showCancellation([item], false, false);
    }

    const onCancelGroupClick = (selectedGroupId: string) => {
        setSelectedGroup(selectedGroupId);
        store.showCancellation([...group.items], false, false);
    }

    // Because React and Mobx suck together
    // Entire process here is a hack. Shouldn't need to have a useState for the group this component knows it has.
    const onRescheduleItemClick = (item: IAppointmentManagementItem, selectedGroupId: string) => {
        setSelectedGroup(selectedGroupId);
        if (!group.rescheduleViaVOB) {
            store.showCancellation([item], true, false);
        } else {
            // singular rescheduled item.
            setRescheduleItem(item);

            requestAnimationFrame(() => {
                document.getElementById(groupId)?.click();
            });
        }
    }

    const onRescheduleGroupClick = (rebook: boolean, selectedGroupId: string) => {
        setSelectedGroup(selectedGroupId);
        if (!group.rescheduleViaVOB) {
            if (!rebook) {
                store.showCancellation([...group.items], true, rebook);
            } else {
                // Map returns Array<string | undefined>
                // Compact strips out all falsey values including undefined and ""
                const ids = _.compact(_.map(group.items, 'reservationPublicId'));
                store.cancelAppointmentGroup(ids, true, rebook).then(() => {
                    if (mounted) {
                        store.getAppointmentAndVisitData().then(() => {
                            document.getElementById(groupId)?.click();
                        }).catch(() => {
                            if (mounted)
                                navigate("/verification");
                        });
                    }
                });
            }
        } else {
            setRescheduleItem(undefined);
            document.getElementById(groupId)?.click();
        }
    }

    //#endregion

    useEffect(() => {
        if (!mounted)
            return; //don't do anything
        return () => {
            mounted.current = false;
        };
    }, [mounted]);

    const resendVisitConfirmationEmail = async (items: IAppointmentManagementItem[]) => {
        if (items.length && items.some(i => i.visitPublicId !== undefined)) {
            const visitIds: string[] = [];
            items.forEach(item => {
                if (item.visitPublicId !== undefined) visitIds.push(item.visitPublicId);
            });
            await store.resendVisitCompletionEmail(visitIds);
            setResendEmail(false);
        }
    }

    return (
        <>
            <Card className="mt-1 mb-4">
                <Card.Body>
                    <span className={
                        classNames({
                            'text-success': group.status == 'Confirmed' || group.status == 'Completed',
                            'text-danger': group.status == 'Missed' || group.status == 'Cancelled' || group.status == 'Clinic Cancelled'
                        },
                            'fs-6')}>
                        {group.status}
                    </span>

                    <p className="fw-bold">{`${DateTime.fromJSDate(clinicDate).toFormat("EEEE, MMMM")} ${ordinal.getNumberOrdinal(clinicDate.getDate())} `}</p>
                    <ListGroup variant="flush" className="pb-1">
                        <ListGroup.Item className="px-2">
                            <Row className="pb-2">
                                <Col xs={1}>
                                    <div className="d-flex align-items-center justify-content-center appt-location-icon">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                                    </div>
                                </Col>
                                <Col xs={11} className="px-0">
                                    <div className="sm-type mx-0">
                                        <p className="fw-bold">At {group.partnerName}</p>
                                        <p>{group.address}</p>
                                        <p>{group.city}, {group.state} {group.zip}</p>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12}>
                                    <Table>
                                        <thead>
                                            <tr key={"header"} className="appt-tbl-head">
                                                <th></th>
                                                <th></th>
                                                {group.canCancel ? <th></th> : null}
                                                {group.canReschedule ? <th></th> : null}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                group.items.map((item) => (
                                                    <tr key={`${item.reservationPublicId}_${item.visitPublicId}_${item.itemTime}`} className="appt-tbl-row">
                                                        <td>{item.petName}</td>
                                                        <td>at <b className="primary">{item.itemTime}</b></td>
                                                        {group.canCancel ?
                                                            <td>
                                                                <a className="primary clickable" onClick={() => { onCancelItemClick(item, groupId) }}>Cancel</a>
                                                            </td>
                                                            : null}
                                                        {group.canReschedule ?
                                                            <td>
                                                                {(group.isHospitalReservation && !configuration.vobRescheduleEnabled) ?
                                                                    <></>
                                                                    :
                                                                    <a className="primary clickable" onClick={() => { onRescheduleItemClick(item, groupId) }}>Reschedule</a>
                                                                }
                                                            </td>
                                                            : null}
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        </ListGroup.Item>
                    </ListGroup>

                    <div className="d-grid gap-2 mb-2">
                        {group.canConfirm ?
                            <Button variant="primary" className="text-uppercase btn-customer" type="button" onClick={() => { onConfirmGroupClick() }}>Confirm</Button>
                            : null}
                        {group.canSendVisitCompletion ?
                            <Button variant="primary" className="text-uppercase btn-customer" onClick={() => setResendEmail(true)}>Resend Your Post Visit Summary</Button>
                            : null}
                        {group.canRebook ?
                            <Button variant="primary" className="text-uppercase btn-customer" onClick={() => { onRescheduleGroupClick(true,groupId) }}>Rebook Appointment</Button>
                            : null}
                    </div>

                    <div className={classNames("d-flex gap-2", { 'flex-wrap': (group.isHospitalReservation && !configuration.vobRescheduleEnabled) })}>
                        {group.canCancel ?
                            <Button variant="light" className="text-uppercase w-100 flex-fill btn-customer" onClick={() => { onCancelGroupClick(groupId) }}>Cancel</Button>
                            : null}
                        {group.canReschedule ?
                            <>
                                {(group.isHospitalReservation && !configuration.vobRescheduleEnabled) ?
                                    <p className="text-center mt-3 w-100">To reschedule, call {normalizePhone(group.hospitalPhone)}</p>
                                    :
                                    <Button variant="light" className="text-uppercase w-100 flex-fill btn-customer" onClick={() => { onRescheduleGroupClick(false, groupId) }}>Reschedule</Button>
                                }
                            </>
                            : null}
                    </div>
                </Card.Body>
                {
                    group.canViewIntake ?
                        <Card.Footer>
                            <div className="sm-type mb-2">
                                <p>
                                    <b>
                                        Help us get ready for you pet&apos;s appointment
                                    </b>
                                </p>
                                <p>
                                    Please review or complete your Pet&apos;s Intake Form prior to your visit. This information will help our team provide the best visit tailored to your pet&apos;s needs.
                                </p>
                            </div>
                            <div className="d-flex gap-2 mb-2">
                                <Button variant="primary" className="text-uppercase flex-fill btn-customer" onClick={() => { window.open(`/customer/intake/${group.intakePublicId}?linksource=customerapp`) }}>Complete Form</Button>
                            </div>
                        </Card.Footer>
                        : null
                }
            </Card>
            {
                group.rescheduleViaVOB ?
                    <>
                        <form
                            action={group.vobRescheduleUrl}
                            name={'rebook' + group.clinicId}
                            id={'rebook' + group.clinicId}
                            method="POST"
                            style={{ display: "none" }}
                            target="_blank">
                            <input type="text" readOnly hidden={true} name="firstname" value={group.firstName} />
                            <input type="text" readOnly hidden={true} name="lastname" value={group.lastName} />
                            <input type="text" readOnly hidden={true} name="phone" value={group.phone} />
                            <input type="text" readOnly hidden={true} name="email" value={group.email} />
                            <input type="text" readOnly hidden={true} name="isreturningcustomer" value={group.wasPriorCustomer.toString()} />
                            <input type="text" readOnly hidden={true} name="store_number" value={group.storeCode} />
                            <input type="text" readOnly hidden={true} name="zipcode" value={group.zip} />
                            <input type="text" readOnly hidden={true} name="appointmentTypeId" value={group.publicAppointmentTypeId} />
                            {_.isUndefined(rescheduleItem) ?
                                group.items.map((item) => (
                                    <div key={item.reservationPublicId}>
                                        <input type="text" readOnly hidden={true} name="petname" value={item.petName} />
                                        <input type="text" readOnly hidden={true} name="petspecies" value={item.species} />
                                        <input type="text" readOnly hidden={true} name="reschedulingReservationId" value={item.reservationPublicId} />
                                    </div>
                                ))
                                :
                                <>
                                    <input type="text" readOnly hidden={true} name="petname" value={rescheduleItem.petName} />
                                    <input type="text" readOnly hidden={true} name="petspecies" value={rescheduleItem.species} />
                                    <input type="text" readOnly hidden={true} name="reschedulingReservationId" value={rescheduleItem.reservationPublicId} />
                                </>
                            }

                            <button type="submit" id={groupId}>
                                Re-Book Appointment
                            </button>
                        </form>
                    </>
                    :
                    <>
                        <form
                            action={store.jackpotReservationBookingUrl}
                            name={'rebook' + group.clinicId}
                            id={'rebook' + group.clinicId}
                            method="POST"
                            style={{ display: "none" }}
                            target="_blank">
                            <input type="text" readOnly hidden={true} name="firstname" value={group.firstName} />
                            <input type="text" readOnly hidden={true} name="lastname" value={group.lastName} />
                            <input type="text" readOnly hidden={true} name="phone" value={group.phone} />
                            <input type="text" readOnly hidden={true} name="email" value={group.email} />
                            <input type="text" readOnly hidden={true} name="isreturningcustomer" value={group.wasPriorCustomer.toString()} />
                            <button type="submit" id={groupId}>
                                Re-Book Appointment
                            </button>
                        </form>
                    </>
            }

            {group.canConfirm ?
                <ConfirmReservationModal
                    visible={store.confirmationVisible}
                    bodyText={confirmationBody}
                    onConfirm={store.confirm} />
                : null}

            {((group.canCancel || group.canReschedule ) && groupId===selectedGroup) ?
                <CancelReservationModal
                    visible={store.cancellationVisible}
                    bodyText={cancellationBody}
                    onConfirm={() => {
                        store.cancelReservationConfirm();

                        if (!group.rescheduleViaVOB)
                            document.getElementById(groupId)?.click();
                    }}
                    onCancel={store.closeCancellation} />
                : null}

            <YesNoConfirmModal
                visible={resendEmail}
                busy={anyPromiseTrackerTagsInProgress([VisitService.tags.resendVisitCompletionEmail], 500)}
                title={'Resend Your Post Visit Summary'}
                description={`Are you sure you want to resend your Post Visit Summary documents for ${group.items.map(i => i.petName).join(', ')}.`}
                onConfirm={() => resendVisitConfirmationEmail(group.items)}
                onCancel={() => setResendEmail(false)} />
        </>
    )
});
